import { useRef, RefObject } from "react";
import { useVideoConfig, OffthreadVideo, Audio } from "remotion";
import { VideoSegmentMultiFocusDesignSystemConfig } from "../../VideoSegmentMultiFocus";
import { onVideoFrame } from "../DefaultVideoSegmentMultiFocus";
import { BorderWrapper } from "../utils/borderStyle";
import { getCloudFrontUrl } from "../../../../../../helper/CloudFrontUrlHelper";

const TwoSplit16x9: React.FC<VideoSegmentMultiFocusDesignSystemConfig> = ({ startTimestampMS, endTimestampMS, videoURL, spacing, borderRadius, border, borderStyle, framing, designSystem, audioURL }) => {
    const { width, height, fps } = useVideoConfig();
    const resolvedVideoURL = getCloudFrontUrl(videoURL);
    const resolvedAudioURL = audioURL ? getCloudFrontUrl(audioURL) : undefined;

    // We will display two 8:9 videos side by side
    const canvasRefs = [useRef<HTMLCanvasElement>(null), useRef<HTMLCanvasElement>(null)];

    const paddingSize = spacing ? Math.sqrt(width * height) * 0.05 : 0;
    const borderThickness = spacing ? Math.sqrt(width * height) / 150 : 0;

    const totalHeight = height - paddingSize * 2 - borderThickness * 2;
    const totalWidth = totalHeight * (8 / 9);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-evenly',
                alignContent: 'space-evenly',
                width: '100%',
                height: '100%',
            }}
        >
            <OffthreadVideo
                style={{ opacity: 0, display: 'none' }}
                src={resolvedVideoURL}
                startFrom={startTimestampMS / 1000 * fps}
                endAt={endTimestampMS / 1000 * fps}
                onVideoFrame={(frame) => onVideoFrame(frame, framing["16:9"].focusRegions, canvasRefs as RefObject<HTMLCanvasElement>[])}
                muted={Boolean(resolvedAudioURL)}
            />
            {resolvedAudioURL && <Audio src={resolvedAudioURL} />}
            {framing["16:9"].focusRegions.map((_, index) => (
                <BorderWrapper
                    key={index}
                    borderStyle={borderStyle}
                    borderThickness={borderThickness}
                    borderRadius={borderRadius}
                >
                    <canvas
                        ref={canvasRefs[index]}
                        width={totalWidth}
                        height={totalHeight}
                        style={{
                            aspectRatio: '8 / 9',
                        }}
                    />
                </BorderWrapper>
            ))}
        </div>
    );
}

const TwoSplit9x16: React.FC<VideoSegmentMultiFocusDesignSystemConfig> = ({ startTimestampMS, endTimestampMS, videoURL, spacing, borderRadius, border, borderStyle, framing, designSystem, audioURL }) => {
    const { width, height, fps } = useVideoConfig();
    const resolvedVideoURL = getCloudFrontUrl(videoURL);
    const resolvedAudioURL = audioURL ? getCloudFrontUrl(audioURL) : undefined;

    // We will display two 9:8 videos side by side
    const canvasRefs = [useRef<HTMLCanvasElement>(null), useRef<HTMLCanvasElement>(null)];

    const paddingSize = spacing ? Math.sqrt(width * height) * 0.05 : 0;
    const borderThickness = spacing ? Math.sqrt(width * height) / 150 : 0;

    const totalWidth = width - paddingSize * 2 - borderThickness * 2;
    const totalHeight = totalWidth * (8 / 9);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-evenly',
                alignContent: 'space-evenly',
                width: '100%',
                height: '100%',
            }}
        >
            <OffthreadVideo
                style={{ opacity: 0, display: 'none' }}
                src={resolvedVideoURL}
                startFrom={startTimestampMS / 1000 * fps}
                endAt={endTimestampMS / 1000 * fps}
                onVideoFrame={(frame) => onVideoFrame(frame, framing["9:16"].focusRegions, canvasRefs as RefObject<HTMLCanvasElement>[])}
                muted={Boolean(resolvedAudioURL)}
            />
            {resolvedAudioURL && <Audio src={resolvedAudioURL} />}
            {framing["9:16"].focusRegions.map((_, index) => (
                <BorderWrapper
                    key={index}
                    borderStyle={borderStyle}
                    borderThickness={borderThickness}
                    borderRadius={borderRadius}
                >
                    <canvas
                        ref={canvasRefs[index]}
                        width={totalWidth}
                        height={totalHeight}
                        style={{
                            aspectRatio: '9 / 8',
                        }}
                    />
                </BorderWrapper>
            ))}
        </div>
    );
}

const TwoSplit1x1: React.FC<VideoSegmentMultiFocusDesignSystemConfig> = ({ startTimestampMS, endTimestampMS, videoURL, spacing, borderRadius, border, borderStyle, framing, designSystem, audioURL }) => {
    const { width, height, fps } = useVideoConfig();
    const resolvedVideoURL = getCloudFrontUrl(videoURL);
    const resolvedAudioURL = audioURL ? getCloudFrontUrl(audioURL) : undefined;

    // We will display two 2:1 videos side by side
    const canvasRefs = [useRef<HTMLCanvasElement>(null), useRef<HTMLCanvasElement>(null)];

    const paddingSize = spacing ? Math.sqrt(width * height) * 0.05 : 0;
    const borderThickness = spacing ? Math.sqrt(width * height) / 150 : 0;

    const totalHeight = height - paddingSize * 2 - borderThickness * 2;
    const totalWidth = totalHeight * (2 / 1);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-evenly',
                alignContent: 'space-evenly',
                width: '100%',
                height: '100%',
            }}
        >
            <OffthreadVideo
                style={{ opacity: 0, display: 'none' }}
                src={resolvedVideoURL}
                startFrom={startTimestampMS / 1000 * fps}
                endAt={endTimestampMS / 1000 * fps}
                onVideoFrame={(frame) => onVideoFrame(frame, framing["1:1"].focusRegions, canvasRefs as RefObject<HTMLCanvasElement>[])}
                muted={Boolean(resolvedAudioURL)}
            />
            {resolvedAudioURL && <Audio src={resolvedAudioURL} />}
            {framing["1:1"].focusRegions.map((_, index) => (
                <BorderWrapper
                    key={index}
                    borderStyle={borderStyle}
                    borderThickness={borderThickness}
                    borderRadius={borderRadius}
                >
                    <canvas
                        ref={canvasRefs[index]}
                        width={totalWidth}
                        height={totalHeight}
                        style={{
                            aspectRatio: '2 / 1',
                        }}
                    />
                </BorderWrapper>
            ))}
        </div>
    );
}

export const TwoSplitFocusVideoSegment: React.FC<VideoSegmentMultiFocusDesignSystemConfig> = (props) => {
    switch (props.designSystem.canvas.aspectRatio) {
        case "16:9":
            return <TwoSplit16x9 {...props} />
        case "9:16":
            return <TwoSplit9x16 {...props} />
        case "1:1":
            return <TwoSplit1x1 {...props} />
        default:
            throw new Error(`Unsupported aspect ratio: ${props.designSystem.canvas.aspectRatio}`);
    }
}
